import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppConstants } from '../shared/constants/app-constants';

interface ZendeskSnippetResponse {
  snippet_id?: string;
}

@Injectable({ providedIn: 'root' })
export class ZendeskWidgetService {
  private static readonly DEFAULT_SNIPPET_KEY =
    '36cd59ff-0a75-4140-8698-24e0ec8ac808';

  private loadingPromise: Promise<void> | null = null;

  constructor(private http: HttpClient) {}

  load(): Promise<void> {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }
    this.loadingPromise = this.loadInternal().catch(() => undefined) as Promise<void>;
    return this.loadingPromise;
  }

  private async loadInternal(): Promise<void> {
    if (document.getElementById('ze-snippet')) {
      return;
    }
    const keyFromApi = await this.tryGetSnippetKeyFromApi();
    const key = keyFromApi || ZendeskWidgetService.DEFAULT_SNIPPET_KEY;

    await this.injectScript(key);
  }

  private async tryGetSnippetKeyFromApi(): Promise<string | null> {
    try {
      const apiBase = (AppConstants.BASE_URL || '').replace(/\/+$/, '');
      if (!apiBase) {
        return null;
      }
      const url = `${apiBase}/zendesk/snippet-id`;

      const resp = await this.http.get<ZendeskSnippetResponse>(url).toPromise();
      const key = (resp?.snippet_id || '').trim();
      if (!key || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key)) {
        return null;
      }

      return key;
    } catch {
      return null;
    }
  }

  private injectScript(key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.getElementById('ze-snippet')) {
        return resolve();
      }

      const s = document.createElement('script');
      s.id = 'ze-snippet';
      s.async = true;
      s.src = `https://static.zdassets.com/ekr/snippet.js?key=${encodeURIComponent(key)}`;
      s.onload = () => resolve();
      s.onerror = () => resolve();
      document.head.appendChild(s);
    });
  }
}
